// BookingFlowStep3 — the "Contact details + Send" step of the
// progressive booking flow. Rendered inline once the user has cleared
// step 2. The actual submission lives in the shared BookingContext so
// the same payload is generated regardless of which surface (widget /
// /booking page / sticky summary) triggers the send.
//
// Extracted from `HomeBookingWidget.jsx`.

import { forwardRef } from "react";
import { ArrowRight, Loader2, Send } from "lucide-react";
import { DetailsStep } from "@/components/booking/BookingSteps";
import { COPY } from "@/components/booking/homeBookingWidgetCopy";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const BookingFlowStep3 = forwardRef(function BookingFlowStep3(
  {
    lang,
    isSubmitting,
    onBack,
    onSend,
    nameRef,
    emailRef,
    phoneRef,
  },
  ref
) {
  return (
    <div
      ref={ref}
      className="mt-10 pt-8 border-t border-[#D4A373]/40"
      data-testid="home-booking-step-3"
    >
      <div className="mb-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
          {pick(COPY.step3_overline, lang)}
        </p>
        <h3 className="mt-1 font-serif-x text-2xl md:text-3xl text-[#2C2621] leading-tight">
          {pick(COPY.step3_title, lang)}
        </h3>
        <p className="mt-2 text-sm text-[#5C5248] leading-relaxed max-w-2xl">
          {pick(COPY.step3_sub, lang)}
        </p>
      </div>

      <DetailsStep
        lang={lang}
        nameRef={nameRef}
        emailRef={emailRef}
        phoneRef={phoneRef}
      />

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] inline-flex items-center gap-1.5"
          data-testid="home-booking-back-2"
        >
          <ArrowRight className="w-3.5 h-3.5 rotate-180" strokeWidth={1.7} />
          {pick(COPY.cta_back, lang)}
        </button>
        <div className="flex items-center gap-4">
          <p className="text-[11px] text-[#5C5248] max-w-[20rem]">
            {pick(COPY.no_deposit, lang)}
          </p>
          <button
            type="button"
            onClick={onSend}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors whitespace-nowrap"
            data-testid="home-booking-send-inquiry"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" strokeWidth={1.6} />
            )}
            {isSubmitting ? pick(COPY.cta_sending, lang) : pick(COPY.cta_send, lang)}
          </button>
        </div>
      </div>
    </div>
  );
});

export default BookingFlowStep3;
